'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { EntityDataService } from './EntityDataService';
import { ApiProjectAccessDto } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import { log } from '@/lib/utils/logger';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { ProjectSelectionDialog } from '@/components/dialog/ProjectSelectionDialog';
import { ProjectRowType } from '@/components/tables/ProjectGridCore';

interface ProjectAccessDialogProps {
  open: boolean;
  projectAccess: ApiProjectAccessDto[];
  onSave: (projectAccess: ApiProjectAccessDto[]) => void;
  onClose: () => void;
}

interface EditableProjectAccess extends ApiProjectAccessDto {
  isEditing?: boolean;
  tempProjectId?: number;
  tempProjectLabel?: string;
  tempRoleId?: number;
}

const OPTION_KEYS: OptionKey[] = ['projects']; //, 'projectSearchStatus', 'projectStatus'];

export default function ProjectAccessDialog({ open, projectAccess, onSave, onClose }: ProjectAccessDialogProps) {
  const backend = useBackend();
  const { options } = useSelectOptions(OPTION_KEYS);
  const [projectAccessList, setProjectAccessList] = useState<EditableProjectAccess[]>(
    projectAccess.map((item) => ({ ...item, isEditing: false }))
  );
  const [roles, setRoles] = useState<Array<{ value: number; label: string }>>([]);
  const [isProjectSelectionOpen, setIsProjectSelectionOpen] = useState(false);
  const [editingProjectIndex, setEditingProjectIndex] = useState<number | null>(null);

  // Load roles when dialog opens
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const dataService = new EntityDataService(backend);

        // Load roles (filter to only show non-user roles)
        const rolesData = await dataService.loadData('roles');
        const nonUserRoles = rolesData.filter((role: any) => !role.isUser);
        setRoles(nonUserRoles.map((role: any) => ({ value: role.id, label: role.name })));
      } catch (error) {
        log.error('Failed to load roles:', error);
      }
    };

    if (open) {
      loadRoles();
    }
  }, [open, backend]);

  const handleAddProjectAccess = () => {
    const newEntry: EditableProjectAccess = {
      projectId: 0,
      projectLabel: '',
      roleId: 0,
      isEditing: true,
      tempProjectId: 0,
      tempProjectLabel: '',
      tempRoleId: 0,
    };
    setProjectAccessList([...projectAccessList, newEntry]);
  };

  const handleEditProjectAccess = (index: number) => {
    const updatedList = [...projectAccessList];
    updatedList[index] = {
      ...updatedList[index],
      isEditing: true,
      tempProjectId: updatedList[index].projectId,
      tempProjectLabel: updatedList[index].projectLabel,
      tempRoleId: updatedList[index].roleId,
    };
    setProjectAccessList(updatedList);
  };

  const handleCancelEdit = (index: number) => {
    const updatedList = [...projectAccessList];
    if (updatedList[index].tempProjectId === 0 && updatedList[index].tempProjectLabel === '') {
      // This was a new entry, remove it
      updatedList.splice(index, 1);
    } else {
      // This was an existing entry, revert changes
      updatedList[index] = {
        ...updatedList[index],
        isEditing: false,
        tempProjectId: undefined,
        tempProjectLabel: undefined,
        tempRoleId: undefined,
      };
    }
    setProjectAccessList(updatedList);
  };

  const handleApplyEdit = (index: number) => {
    const entry = projectAccessList[index];
    if (!entry.tempProjectId || !entry.tempRoleId) {
      return; // Validation
    }

    const updatedList = [...projectAccessList];
    updatedList[index] = {
      projectId: entry.tempProjectId,
      projectLabel: entry.tempProjectLabel || '',
      roleId: entry.tempRoleId,
      isEditing: false,
      tempProjectId: undefined,
      tempProjectLabel: undefined,
      tempRoleId: undefined,
    };
    setProjectAccessList(updatedList);
  };

  const handleDeleteProjectAccess = (index: number) => {
    const updatedList = projectAccessList.filter((_, i) => i !== index);
    setProjectAccessList(updatedList);
  };

  const handleProjectChange = (index: number, projectId: number, projectLabel: string) => {
    const updatedList = [...projectAccessList];
    updatedList[index] = {
      ...updatedList[index],
      tempProjectId: projectId,
      tempProjectLabel: projectLabel,
    };
    setProjectAccessList(updatedList);
  };

  const handleProjectLookup = (index: number) => {
    // Only open the dialog if we have the minimum required options loaded
    // This prevents the ProjectGridCore from trying to fetch data before options are ready
    if (options.projects && options.projects.length > 0) {
      setEditingProjectIndex(index);
      setIsProjectSelectionOpen(true);
    }
  };

  const handleProjectSelect = (project: ProjectRowType) => {
    if (editingProjectIndex !== null) {
      handleProjectChange(editingProjectIndex, project.id, project.projectLabel);
    }
    setIsProjectSelectionOpen(false);
    setEditingProjectIndex(null);
  };

  const handleRoleChange = (index: number, roleId: number) => {
    const updatedList = [...projectAccessList];
    updatedList[index] = {
      ...updatedList[index],
      tempRoleId: roleId,
    };
    setProjectAccessList(updatedList);
  };

  const handleApply = () => {
    // Filter out any entries that are still being edited
    const validEntries = projectAccessList.filter((entry) => !entry.isEditing);
    onSave(validEntries);
    onClose();
  };

  const getRoleName = (roleId: number) => {
    const role = roles.find((r) => r.value === roleId);
    return role?.label || 'Unknown Role';
  };

  const canApply = () => {
    // Check if any entry is being edited
    const hasEditingEntry = projectAccessList.some((entry) => entry.isEditing);
    if (hasEditingEntry) {
      return false;
    }
    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Project Access</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Add button */}
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleAddProjectAccess} sx={{ mb: 2 }}>
            Add Project Access
          </Button>

          {/* Project access list */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectAccessList.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {entry.isEditing ? (
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <Autocomplete
                            fullWidth
                            options={options.projects || []}
                            getOptionLabel={(option: any) => option?.projectLabel || ''}
                            value={options.projects?.find((p: any) => p.id === entry.tempProjectId) || null}
                            onChange={(_, newValue) => {
                              if (newValue && newValue.id) {
                                handleProjectChange(index, newValue.id, newValue.projectLabel || '');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Search projects..."
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: params.InputProps.endAdornment,
                                }}
                              />
                            )}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleProjectLookup(index)}
                            startIcon={<SearchIcon />}
                            sx={{ minWidth: 'auto', px: 1, py: 1.5 }}
                            title={
                              !options.projects || options.projects.length === 0
                                ? 'Loading projects...'
                                : 'Search and select projects'
                            }
                            disabled={!options.projects || options.projects.length === 0}
                          >
                            {!options.projects || options.projects.length === 0 ? 'Loading...' : 'Lookup'}
                          </Button>
                        </Box>
                      ) : (
                        entry.projectLabel || 'Unknown Project'
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={entry.tempRoleId || ''}
                            onChange={(e) => handleRoleChange(index, e.target.value as number)}
                          >
                            {roles.map((role) => (
                              <MenuItem key={role.value} value={role.value}>
                                {role.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        getRoleName(entry.roleId!)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {entry.isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleApplyEdit(index)}
                            disabled={!entry.tempProjectId || !entry.tempRoleId}
                            title="Apply"
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleCancelEdit(index)} title="Cancel">
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleEditProjectAccess(index)} title="Edit">
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteProjectAccess(index)} title="Delete">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {projectAccessList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No project access entries
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained" disabled={!canApply()}>
          Apply
        </Button>
      </DialogActions>

      {/* Project Selection Dialog */}
      <ProjectSelectionDialog
        open={isProjectSelectionOpen}
        title="Select Project"
        onProjectSelect={handleProjectSelect}
        onProjectsSelect={() => {}} // Not needed for single selection
        onClose={() => {
          setIsProjectSelectionOpen(false);
          setEditingProjectIndex(null);
        }}
        allowMultiple={false}
      />
    </Dialog>
  );
}
